import {
  Button,
  DefaultPageSkeleton,
  FormRow,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
  Title
} from '@efast_public/fjd-component-library';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useNotification } from '../../shared/utils/notification/useNotification';
import { ApiRoutes, mandantClient } from '../../api/mandant-api';
import { UpdateProfileRequest} from '../../api/mandant.types';
import {useProfile} from "../../shared/utils/auth/AuthContext";
import DeleteUsersAndRolesManagementDeleteDialog
  from "../../modals/UsersAndRolesManagementDeleteDialogModal";
import {useState} from "react";

interface EditProfileFormData {
  salutation?: string;
  titleField?: string;
  organisation?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}


export function EditProfilePage() {
  const { t } = useTranslation('mc');
  const navigate = useNavigate();
  const { notifyUserFromRequest } = useNotification();
  const { profile } = useProfile();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty }
  } = useForm<EditProfileFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    values: profile
      ? {
          salutation: '',
          titleField: '',
          organisation: '',
          firstName: profile.firstName ?? '',
          lastName: profile.lastName ?? '',
          email: profile.email ?? ''
        }
      : undefined
  });

  const { mutate: updateProfile, isPending } = useMutation({
    mutationFn: (data: UpdateProfileRequest) =>
      notifyUserFromRequest(
        () => mandantClient.put(ApiRoutes.profile(), data),
        {
          successMessage: t('api.success.updateProfile'),
          errorMessage: t('api.error.updateProfile')
        }
      )
  });

  const onSubmit = (data: EditProfileFormData) => {
    updateProfile({
      salutation: data.salutation ?? '',
      title: data.titleField ?? '',
      organisation: data.organisation ?? '',
      firstName: data.firstName ?? '',
      lastName: data.lastName ?? '',
      username: ''
    });
  };

  const salutationOptions = [
    { label: t('addUserPage.salutation.mr'), value: 'mr' },
    { label: t('addUserPage.salutation.mrs'), value: 'mrs' },
    { label: t('addUserPage.salutation.diverse'), value: 'diverse' }
  ];

  const titleOptions = [
    { label: t('addUserPage.title_field.dr'), value: 'dr' },
    { label: t('addUserPage.title_field.prof'), value: 'prof' },
    { label: t('addUserPage.title_field.profDr'), value: 'profDr' }
  ];

  return (
    <div style={{ maxWidth: '680px', margin: '0 auto', padding: '40px 20px' }}>
      <Stack direction="column" spacing="l">
        <Title level={1} visualLevel={3}>
          {t('editProfilePage.title')}
        </Title>

        <form>
          <Stack direction="column" spacing="m">
              <Text strong size="m">
                {t('editProfilePage.sectionPersonal')}
              </Text>

              <FormRow label={t('editProfilePage.email')}>
                <Input
                  data-testid="email-input"
                  type="text"
                  style={{ width: '100%' }}
                  {...register('email')}
                />
              </FormRow>

              <FormRow
                label={t('editProfilePage.salutation')}
                validationMessage={errors.salutation?.message}
                variant={errors.salutation ? 'error' : undefined}
              >
                <Controller
                  name="salutation"
                  control={control}
                  render={({ field }) => (
                    <Select
                      data-testid="salutation-select"
                      options={salutationOptions}
                      selectedOptions={
                        field.value
                          ? salutationOptions.filter((o) => o.value === field.value)
                          : []
                      }
                      onChange={(selected) => field.onChange(selected[0]?.value ?? '')}
                    />
                  )}
                />
              </FormRow>

              <FormRow
                label={t('editProfilePage.title_field')}
                validationMessage={errors.titleField?.message}
                variant={errors.titleField ? 'error' : undefined}
              >
                <Controller
                  name="titleField"
                  control={control}
                  render={({ field }) => (
                    <Select
                      data-testid="title-select"
                      options={titleOptions}
                      selectedOptions={
                        field.value
                          ? titleOptions.filter((o) => o.value === field.value)
                          : []
                      }
                      onChange={(selected) => field.onChange(selected[0]?.value ?? '')}
                    />
                  )}
                />
              </FormRow>

              <FormRow label={t('editProfilePage.organisation')}>
                <Input
                  data-testid="organisation-input"
                  type="text"
                  style={{ width: '100%' }}
                  {...register('organisation')}
                />
              </FormRow>

              <FormRow label={t('editProfilePage.firstName')}>
                <Input
                  data-testid="firstName-input"
                  type="text"
                  style={{ width: '100%' }}
                  {...register('firstName')}
                />
              </FormRow>

              <FormRow label={t('editProfilePage.lastName')}>
                <Input
                  data-testid="lastName-input"
                  type="text"
                  style={{ width: '100%' }}
                  {...register('lastName')}
                />
              </FormRow>

              <Button
                data-testid="submit-button"
                type="button"
                onClick={handleSubmit(onSubmit)}
                variant="primary"
                disabled={isPending || !isValid || !isDirty}
              >
                {isPending ? <Spinner size="sm" /> : t('editProfilePage.submit')}
              </Button>
            </Stack>
        </form>
      </Stack>
      {isModalVisible && <DeleteUsersAndRolesManagementDeleteDialog isOpen onClose={() => setIsModalVisible(false)} userId={profile?.id ?? ''} />}
    </div>
  );
}
